import { addHours, format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { IconButton, Button, InputAdornment, TextField, LinearProgress } from '@mui/material'

import Dialog from '../common/dialog'
import { config } from '../../config'
import { countriesCurrencies } from '../../lib'
import { useSocket } from '../../hooks/use-socket'
import HelpIcon from '../../assets/icons/help-icon.svg'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { FormError, Payment } from '../../types'
import SearchIcon from '../../assets/icons/search-icon.svg'
import Pagination from '../../components/common/pagination'
import DownloadIcon from '../../assets/icons/download-icon.svg'
import { getCustomerCompanyPaymentsData } from '../../api/app-data.api'
import { checkTwoFactorAuth, requestAccessToken } from '../../api/auth.api'
import { baseButtonBlueBg, textFieldWhiteBg } from '../../theme/custom-styles'
import { getJsonSize, normalizePaymentData, upsertPaymentWithLimitAtStart } from '../../utils'
import { CustomerCompanyPaymentsFilter } from './customer-company-payments-filter'
import { mergeAndSortPayments, TablePayment } from '../../utils/customer-company.utils'
import { handleCheckIfOtpCodeIsValid, handleIsValidMatch } from '../../utils/common.utils'
import DocumentPaymentListItem from '../document-payments-details-management/document-payment-list-item'
import { PaymentsHistoryModal } from '../../components/insights-details-management/payments-history-modal'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../../contexts'
import { discardReviewedPayment, downloadPaymentVoucher, getCustomerCompanyPaymentsBySearch, sendReviewedPayment } from '../../api/payments.api'
import CustomerDetailsPaymentDetailsDataTable from '../customer-details-management/customer-details-payment-details-data-table'

export const CustomerCompanyPaymentsHistory = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const location = useLocation()
  const { customerCompanyId } = useParams()
  const { userSession } = useContext(UserContext)
  const { setScrollToTop } = useContext(AppStateContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { isAuth, activateAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customer-company-details-management', 'payments-to-review-management', 'customer-details-management', 'document-payments-details-management'])

  const currentListPageRef = useRef<number>(1)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const customerCompanyPaymentToSearchRef = useRef<string>('')
  const currentCustomerCompanyPaymentsDataRef = useRef<Array<Payment | Partial<Payment>>>([])

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [toDateToSearch, setToDateToSearch] = useState<Date | null>(null)
  const [beneficiaryToSearch, setBeneficiaryToSearch] = useState<string>('')
  const [fromDateToSearch, setFromDateToSearch] = useState<Date | null>(null)
  const [loadingCustomersData, setLoadingCustomersData] = useState<boolean>(true)
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [wantsToDiscardPayment, setWantsToDiscardPayment] = useState<boolean>(false)
  const [paymentDetailsIsLoading, setPaymentDetailsIsLoading] = useState<boolean>(false)
  const [openPaymentFiltersModal, setOpenPaymentFiltersModal] = useState<boolean>(false)
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [openPaymentsHistoryModal, setOpenPaymentsHistoryModal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [customerCompanyPaymentToSearch, setCustomerCompanyPaymentToSearch] = useState<string>('')
  const [openSendPaymentConfimationModal, setOpenSendPaymentConfimationModal] = useState<boolean>(false)
  const [customerCompanyPaymentsList, setCustomerCompanyPaymentsList] = useState<Array<Partial<Payment>>>([])
  const [customerCompanyPaymentsData, setCustomerCompanyPaymentsData] = useState<Array<Partial<Payment>>>([])
  const [dialogSendPaymentConfimationIsLoading, setDialogSendPaymentConfimationIsLoading] = useState<boolean>(false)
  const [searchedCustomerCompanyPayments, setSearchedCustomerCompanyPayments] = useState<Array<Partial<Payment>>>([])

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`newPaymentAdded:${currency}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === true) {
        const localPaymentsBriefData = globalThis.sessionStorage.getItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
        const paymentsDataParsed = JSON.parse(localPaymentsBriefData)
        const paymentsDataUpdated = paymentsDataParsed.paymentsData.filter((paymentData: Payment) => String(paymentData._id) !== String(dataFromSocket._id))

        handleSetInitialData({ paymentsData: paymentsDataUpdated })
        return
      }

      const localPaymentsBriefData = globalThis.sessionStorage.getItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
      const paymentsDataParsed = JSON.parse(localPaymentsBriefData)
      const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsDataParsed.paymentsData, dataFromSocket, 500)

      handleSetInitialData({ paymentsData: paymentsDataUpdated })
    })

    appDataSocket.on(`paymentUpdated:${currency}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === true) {
        const localPaymentsBriefData = globalThis.sessionStorage.getItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
        const paymentsDataParsed = JSON.parse(localPaymentsBriefData)
        const paymentsDataUpdated = paymentsDataParsed.paymentsData.filter((paymentData: Payment) => String(paymentData._id) !== String(dataFromSocket._id))

        handleSetInitialData({ paymentsData: paymentsDataUpdated })
        return
      }

      const localPaymentsBriefData = globalThis.sessionStorage.getItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
      const paymentsDataParsed = JSON.parse(localPaymentsBriefData)
      const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsDataParsed.paymentsData, dataFromSocket, 500)

      handleSetInitialData({ paymentsData: paymentsDataUpdated })
    })

    appDataSocket.on(`paymentDeleted:${currency}`, async (dataFromSocket: any) => {
      const localPaymentsBriefData = globalThis.sessionStorage.getItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
      const paymentsDataParsed = JSON.parse(localPaymentsBriefData)
      const paymentsDataUpdated = paymentsDataParsed.paymentsData.filter((paymentData: Payment) => String(paymentData._id) !== String(dataFromSocket.paymentId))

      handleSetInitialData({ paymentsData: paymentsDataUpdated })
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetCustomerCompanyPaymentsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage =
      customerCompanyPaymentToSearch === '' && searchedCustomerCompanyPayments.length === 0 && !fromDateToSearch && !toDateToSearch
        ? customerCompanyPaymentsData.slice(firstPageIndex, lastPageIndex)
        : searchedCustomerCompanyPayments.slice(firstPageIndex, lastPageIndex)

    setCustomerCompanyPaymentsList(paymentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, customerCompanyPaymentsData, searchedCustomerCompanyPayments])

  const handleGetCustomerCompanyPaymentsData = async () => {
    let localCustomerCompanyPaymentsDataToSet: any = {}
    const localPaymentsBriefData = globalThis.sessionStorage.getItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`)

    if (localPaymentsBriefData) {
      const JSONSize = getJsonSize(localPaymentsBriefData)
      console.log('JSONSize', JSONSize)
      localCustomerCompanyPaymentsDataToSet = JSON.parse(localPaymentsBriefData)
      handleSetInitialData(localCustomerCompanyPaymentsDataToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getCustomerCompanyPaymentsData(selectedCountry, accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`customerCompanyPaymentsData:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetBatchesBriefData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const currentListPageDataState = currentListPageRef.current
    const currentCustomerCompanyPaymentToSearchState = customerCompanyPaymentToSearchRef.current
    const customerCompanyPaymentsNormalizedData = []

    for (const element of dataToSet.paymentsData) {
      const paymentData = { ...element }
      const paymentDataNormalized = normalizePaymentData(paymentData) as Payment

      if (paymentDataNormalized.customerId && !paymentDataNormalized.customerRegisteredFullName) {
        paymentDataNormalized.customerData = {
          registeredFullName: t('empty')
        }
      } else {
        paymentDataNormalized.customerData = paymentData.customerData
      }

      customerCompanyPaymentsNormalizedData.push(paymentDataNormalized)
    }

    customerCompanyPaymentsNormalizedData.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      return Date.parse(String(nextPayment.createdAt)) - Date.parse(String(thisPayment.createdAt))
    })

    if (currentCustomerCompanyPaymentToSearchState === '' || currentCustomerCompanyPaymentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = customerCompanyPaymentsNormalizedData.slice(firstPageIndex, lastPageIndex)

      setCustomerCompanyPaymentsList(paymentsPerPage)
    }

    currentCustomerCompanyPaymentsDataRef.current = customerCompanyPaymentsNormalizedData
    setCustomerCompanyPaymentsData(customerCompanyPaymentsNormalizedData)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    dataFullyLoadedRef.current = true
  }

  const handleGetPaymentsToQuery = () => {
    if (userSession?.role === 'super-admin-client') {
      return {
        deleted: false,
        belongsTo: String(userSession?.belongsTo)
      }
    }

    if (userSession && (userSession.role === 'admin-client' || userSession.role === 'admin-client-builder')) {
      const userSessionAdminOfPockets = Array.isArray(userSession.adminOfPockets) ? userSession.adminOfPockets : []

      return {
        // documentId: '',
        deleted: false,
        belongsTo: String(userSession?.belongsTo),
        fundsFromPocket: { $in: [...userSessionAdminOfPockets] }
      }
    }

    return {
      deleted: false,
      belongsTo: customerCompanyId
    }
  }

  const handleSetCustomerCompanyPaymentToSearch = (customerCompanyPaymentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    customerCompanyPaymentToSearchRef.current = customerCompanyPaymentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setCustomerCompanyPaymentToSearch(customerCompanyPaymentToSearchData)

    if (customerCompanyPaymentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentCustomerCompanyPaymentsDataState = currentCustomerCompanyPaymentsDataRef.current ?? []
      const customerCompanyPaymentsPerPage = currentCustomerCompanyPaymentsDataState.slice(firstPageIndex, lastPageIndex)

      setCustomerCompanyPaymentsList(customerCompanyPaymentsPerPage)
      setSearchedCustomerCompanyPayments([])

      if (showError) setErrors({ customerCompanyPaymentToSearch: t('searchField.errors.emptyField', { ns: 'payments-to-review-management' }) })
      return
    }

    const currentFullCustomerComanyPaymentsData = [...(currentCustomerCompanyPaymentsDataRef.current as Array<Payment>)]
    const filteredCustomerCompanyPayments = currentFullCustomerComanyPaymentsData.filter((paymentData) => {
      const paymentPaxumId = paymentData.additionalValues ? handleIsValidMatch(customerCompanyPaymentToSearchData.toLowerCase(), String(paymentData.additionalValues[0].value).toLowerCase()) : ''
      const customerRegisteredFullName = handleIsValidMatch(customerCompanyPaymentToSearchData.toLowerCase(), String(paymentData.customerRegisteredFullName).toLowerCase())
      const customerDocumentNumber = paymentData.customerData?.documentNumber ? paymentData.customerData.documentNumber : ''
      const paymentReferenceNumber = paymentData.referenceNumber
      const paymentDataDate = format(addHours(new Date(paymentData.createdAt), 5), 'dd/MM/yyyy')

      const customerBanksAccountsData = Array.isArray(paymentData.customerData?.bankAccountsData)
        ? paymentData.customerData?.bankAccountsData.filter((bankAccountData) => {
            return bankAccountData.bankNumber.toLowerCase().includes(customerCompanyPaymentToSearchData.toLowerCase())
          })
        : []

      const paymentStatus =
        paymentData.status === 'validated'
          ? t('paymentStatus.validated', { ns: 'customer-details-management' })
          : paymentData.status === 'reviewed'
          ? t('paymentStatus.reviewed', { ns: 'customer-details-management' })
          : paymentData.status === 'for-review'
          ? t('paymentStatus.forReview', { ns: 'customer-details-management' })
          : paymentData.status === 'waiting-for-resolution'
          ? t('paymentStatus.waitingForResolution', { ns: 'customer-details-management' })
          : paymentData.status === 'register-disperse-completed' || paymentData.status === 'processing'
          ? t('paymentStatus.processing', { ns: 'customer-details-management' })
          : t('paymentStatus.paid', { ns: 'customer-details-management' })

      return (
        String(paymentData.amount).toLowerCase().includes(customerCompanyPaymentToSearchData.toLowerCase()) ||
        paymentStatus.toLowerCase().includes(customerCompanyPaymentToSearchData.toLowerCase()) ||
        paymentDataDate.includes(customerCompanyPaymentToSearchData.toLowerCase()) ||
        customerDocumentNumber.toLowerCase().includes(customerCompanyPaymentToSearchData.toLowerCase()) ||
        paymentReferenceNumber.toLowerCase().includes(customerCompanyPaymentToSearchData.toLowerCase()) ||
        customerBanksAccountsData.length > 0 ||
        customerRegisteredFullName ||
        paymentPaxumId
      )
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const customerCompanyPaymentsPerPage = filteredCustomerCompanyPayments.slice(firstPageIndex, lastPageIndex)

    setCustomerCompanyPaymentsList(customerCompanyPaymentsPerPage)
    setSearchedCustomerCompanyPayments(filteredCustomerCompanyPayments)
  }

  const handleFilterCustomerCompanyPayments = () => {
    setCustomerCompanyPaymentToSearch('')
    handleSetCustomerCompanyPaymentToSearch('', false)

    if (!fromDateToSearch && !toDateToSearch && !beneficiaryToSearch) {
      setOpenPaymentFiltersModal(false)
      return
    }

    if (fromDateToSearch && !toDateToSearch) {
      setErrors({
        toDateToSearch: t('filtersTexts.errors.dates')
      })
      return
    }

    if (!fromDateToSearch && toDateToSearch) {
      setErrors({
        fromDateToSearch: t('filtersTexts.errors.dates')
      })
      return
    }

    if (fromDateToSearch && toDateToSearch && new Date(toDateToSearch).getTime() < new Date(fromDateToSearch).getTime()) {
      setErrors({
        toDateToSearch: t('filtersTexts.errors.wrongDates')
      })
      return
    }

    if (fromDateToSearch || toDateToSearch || beneficiaryToSearch) {
      setErrors({})
      setOpenPaymentFiltersModal(false)
      let customerCompanyPaymentsFiltered = null
      const customerCompanyPaymentsDataToFilter = [...customerCompanyPaymentsData]

      if (fromDateToSearch && toDateToSearch && !beneficiaryToSearch) {
        customerCompanyPaymentsFiltered = customerCompanyPaymentsDataToFilter.filter((customerCompanyPayment) => {
          if (customerCompanyPayment.createdAt) {
            const createdAtTime = new Date(customerCompanyPayment.createdAt).getTime()

            if (createdAtTime < new Date(toDateToSearch).getTime() && createdAtTime > new Date(fromDateToSearch).getTime()) {
              return customerCompanyPayment
            }
          }

          return null
        })

        setSearchedCustomerCompanyPayments(customerCompanyPaymentsFiltered)
      }

      if (!fromDateToSearch && !toDateToSearch && beneficiaryToSearch) {
        customerCompanyPaymentsFiltered = customerCompanyPaymentsDataToFilter.filter((customerCompanyPayment) => {
          if (customerCompanyPayment.customerId === beneficiaryToSearch.split('-')[0]) {
            return customerCompanyPayment
          }

          return null
        })

        setCustomerCompanyPaymentToSearch(beneficiaryToSearch.split('-')[1])
        handleSetCustomerCompanyPaymentToSearch(beneficiaryToSearch.split('-')[1], false)
        setSearchedCustomerCompanyPayments(customerCompanyPaymentsFiltered)
      }

      if (fromDateToSearch && toDateToSearch && beneficiaryToSearch) {
        customerCompanyPaymentsFiltered = customerCompanyPaymentsDataToFilter.filter((customerCompanyPayment) => {
          if (customerCompanyPayment.createdAt) {
            const createdAtTime = new Date(customerCompanyPayment.createdAt).getTime()

            if (createdAtTime < new Date(toDateToSearch).getTime() && createdAtTime > new Date(fromDateToSearch).getTime() && customerCompanyPayment.customerId === beneficiaryToSearch.split('-')[0]) {
              return customerCompanyPayment
            }
          }

          return null
        })

        setCustomerCompanyPaymentToSearch(beneficiaryToSearch.split('-')[1])
        handleSetCustomerCompanyPaymentToSearch(beneficiaryToSearch.split('-')[1], false)
        setSearchedCustomerCompanyPayments(customerCompanyPaymentsFiltered)
      }
    }
  }

  const handleDiscardPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      setErrors({ ...formErrors })
      return
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setOtpCode('')
      setErrors({})

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await discardReviewedPayment(String(selectedPaymentDetails._id), accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-deleted-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleSendPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessTokenData.data.accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setOtpCode('')
      setErrors({})
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await sendReviewedPayment(String(selectedPaymentDetails._id), accessTokenData.data.accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-sent-successfully')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleDownloadPaymentVoucher = async () => {
    try {
      setErrors({})
      setPaymentDetailsIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const paymentVoucherDownloaded = await downloadPaymentVoucher(String(selectedPaymentDetails._id), accessToken)

      window.open(paymentVoucherDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setPaymentDetailsIsLoading(false)
  }

  const handleSearchPaymentDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)
      const {
        data: { accessToken, refreshToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      activateAuth?.({ token: refreshToken })

      const queryToSearch = handleGetPaymentsToQuery()
      const {
        data: { paymentsData }
      } = await getCustomerCompanyPaymentsBySearch({ queryToSearch, customerCompanyPaymentToSearch, belongsTo: String(customerCompanyId) }, accessToken)

      if (paymentsData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      const customerCompanyPaymentsCurrentData = [...customerCompanyPaymentsData]
      const customerCompanyPaymentsUpdatedData = mergeAndSortPayments(paymentsData, customerCompanyPaymentsCurrentData as Array<TablePayment>)
      currentCustomerCompanyPaymentsDataRef.current = customerCompanyPaymentsUpdatedData
      setCustomerCompanyPaymentsData(customerCompanyPaymentsUpdatedData as Array<Payment>)

      setCustomerCompanyPaymentsList(paymentsData)
      setSearchedCustomerCompanyPayments(paymentsData)
      setSearchingInDatabase(false)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      <div className="customer-company-fundings-history-container">
        <div className="actions-bar">
          {/* <div className="payments-history-left-content">
            {customerCompanyPaymentsList.length === 0 ? t('actionsBar.thereIsNoHistory') : `${t('actionsBar.paymentsHistory')} (${customerCompanyPaymentsData.length} ${t('actionsBar.payments')})`}
          </div> */}
          <div className="payments-history-left-content">{customerCompanyPaymentsList.length === 0 ? t('actionsBar.thereIsNoHistory') : `${t('actionsBar.lastPaymentsHistory')}`}</div>
          <div
            className="payments-history-right-content"
            style={
              loadingLocalData || (!loadingLocalData && customerCompanyPaymentsList.length === 0 && location.pathname.indexOf('customer-company-payments-history') === -1)
                ? { justifyContent: 'flex-end' }
                : customerCompanyPaymentsList.length > 0 && location.pathname.indexOf('customer-company-payments-history') === -1
                ? { justifyContent: 'space-between' }
                : customerCompanyPaymentToSearch !== ''
                ? { justifyContent: 'flex-end' }
                : { justifyContent: 'space-between' }
            }
          >
            {!loadingLocalData && customerCompanyPaymentsList.length > 0 && customerCompanyPaymentToSearch === '' && (
              <>
                <div
                  style={{ cursor: 'pointer' }}
                  className="filter-button-container"
                  onClick={() => setOpenPaymentsHistoryModal(true)}
                >
                  <img
                    src={DownloadIcon}
                    alt="download-icon"
                  />
                  <div className="filter-button-text">{t('actionsBar.downloadReport', { ns: 'customer-details-management' })}</div>
                </div>
                {/* <div
                  style={!fromDateToSearch && !toDateToSearch && !beneficiaryToSearch ? { cursor: 'pointer' } : {}}
                  className="filter-button-container"
                  onClick={() => {
                    if (!fromDateToSearch && !toDateToSearch && !beneficiaryToSearch) {
                      setLoadingCustomersData(true)
                      setOpenPaymentFiltersModal(true)
                      setCustomerCompanyPaymentToSearch('')
                      handleSetCustomerCompanyPaymentToSearch('', false)
                      return
                    }
                  }}
                >
                  {!fromDateToSearch && !toDateToSearch && !beneficiaryToSearch ? (
                    <>
                      <img
                        src={FilterIcon}
                        alt="filter-icon"
                      />
                      <div className="filter-button-text">{t('actionsBar.filter', { ns: 'customer-details-management' })}</div>
                    </>
                  ) : (
                    <>
                      <div className="filter-button-text">
                        {beneficiaryToSearch && !fromDateToSearch && !toDateToSearch
                          ? beneficiaryToSearch.split('-')[1]
                          : (fromDateToSearch || toDateToSearch) && !beneficiaryToSearch
                          ? `${format(addHours(new Date(fromDateToSearch as Date), 5), 'dd/MM/yyyy')} - ${format(addHours(new Date(toDateToSearch as Date), 5), 'dd/MM/yyyy')}`
                          : ''}
                      </div>
                      <div
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                          setToDateToSearch(null)
                          setFromDateToSearch(null)
                          setBeneficiaryToSearch('')
                          setOpenPaymentFiltersModal(false)
                          setCustomerCompanyPaymentToSearch('')
                          handleSetCustomerCompanyPaymentToSearch('', false)
                        }}
                      >
                        <img
                          src={CancelIcon}
                          alt="cancel-icon"
                        />
                      </div>
                    </>
                  )}
                </div> */}
              </>
            )}
            {!fromDateToSearch && !toDateToSearch && !beneficiaryToSearch && (
              <TextField
                variant="standard"
                sx={textFieldWhiteBg}
                label={t('searchField.paymentPlaceholder')}
                value={customerCompanyPaymentToSearch}
                error={!!errors.customerCompanyPaymentToSearch}
                helperText={errors.customerCompanyPaymentToSearch}
                className="search-input"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()

                    if (customerCompanyPaymentsList.length === 0) {
                      handleSearchPaymentDataByRegex()
                      return
                    }

                    handleSetCustomerCompanyPaymentToSearch(customerCompanyPaymentToSearch, true)
                  }
                }}
                onChange={(event) => handleSetCustomerCompanyPaymentToSearch(event.target.value, true)}
                onBlur={() => {
                  if (customerCompanyPaymentToSearch === '') setErrors({})
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label="toggle password visibility"
                        sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                      >
                        <img
                          src={SearchIcon}
                          alt="search-icon"
                        />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            )}
          </div>
        </div>
        <div className="table-header-container">
          <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
          <div className="customer-company-table-column customer-company-table-width-45 table-header-text">{t('tableHeaders.beneficiary')}</div>
          <div className="customer-company-table-column customer-company-table-width-15 table-header-text customer-company-display-none-lg">{t('tableHeaders.amount')}</div>
          <div className="customer-company-table-column customer-company-table-width-20 table-header-text customer-company-display-none-sm">{t('tableHeaders.referenceNumber')}</div>
          <div className="customer-company-table-column customer-company-table-width-10 table-header-text">{t('tableHeaders.status')}</div>
        </div>
        {loadingLocalData ? (
          <div className="users-management-users-list-container">
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
          </div>
        ) : (
          <>
            <div className="documents-list-container">
              {customerCompanyPaymentsList.map((paymentData: Partial<Payment> | Payment, index: number) => {
                return (
                  <DocumentPaymentListItem
                    key={index}
                    showDateData
                    showCustomerData
                    paymentData={paymentData as Payment}
                    handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                      if (selectedPaymentData.status === 'waiting-for-resolution') {
                        navigate(`/payment-to-review-details/${String(selectedPaymentData._id)}`, { state: { previousPath: `customer-company-payments-history/${userSession?.belongsTo}` } })
                        return
                      }

                      setOpenPaymentDetailsModal(true)
                      setSelectedPaymentDetails(selectedPaymentData)
                    }}
                  />
                )
              })}
            </div>
            {customerCompanyPaymentToSearch !== '' && customerCompanyPaymentsList.length === 0 && (
              <div className="customer-no-data-found-container">
                {searchingInDatabase === false ? (
                  <>
                    {noRegistriesFoundAtDatabase === false ? (
                      <>
                        <div className="customer-no-data-found-no-registries-text">
                          {t('searchingResultTexts.case1.secondPart')}{' '}
                          <strong
                            className="customer-no-data-found-no-registries-text-click"
                            onClick={() => handleSearchPaymentDataByRegex()}
                          >
                            {t('searchingResultTexts.case1.thirdPart')}
                          </strong>{' '}
                          {t('searchingResultTexts.case1.fourthPart')}
                        </div>
                        <Button
                          sx={baseButtonBlueBg}
                          onClick={handleSearchPaymentDataByRegex}
                        >
                          {t('searchingResultTexts.expandSearch')}
                        </Button>
                        {errors.generalError && (
                          <div
                            style={{ marginTop: '0px' }}
                            className="error-message"
                          >
                            {errors.generalError}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="customer-no-data-found-no-registries-text">
                          <img
                            src={HelpIcon}
                            alt="help-icon"
                          />
                        </div>
                        <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2')}</div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="customer-no-data-found-searching-progress-bar">
                      <LinearProgress />
                    </div>
                    <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3')}</div>
                  </>
                )}
              </div>
            )}
            <Pagination
              pageSize={pageSize}
              currentPage={currentListPage}
              handlePaginationPageChange={(page: number) => {
                setTimeout(() => {
                  setScrollToTop(true)
                  setCurrentListPage(page)
                  currentListPageRef.current = page
                }, 100)
              }}
              handleChangePageSize={(pageSizeToSet: string) => {
                setScrollToTop(true)
                setPageSize(Number(pageSizeToSet))
              }}
              totalCount={
                customerCompanyPaymentToSearch === '' && searchedCustomerCompanyPayments.length === 0 && !fromDateToSearch && !toDateToSearch ? customerCompanyPaymentsData.length : searchedCustomerCompanyPayments.length
              }
            />
          </>
        )}
      </div>
      {openPaymentDetailsModal && (
        <Dialog
          closeOptionWarning={selectedPaymentDetails.status === 'reviewed'}
          dialogIsLoading={paymentDetailsIsLoading}
          handleDialogAction={() => {
            if (selectedPaymentDetails.status === 'reviewed' && userSession && userSession.subRole && userSession.subRole === 'paxum-customer-service-specialist') {
              setOpenPaymentDetailsModal(false)
              setSelectedPaymentDetails({})
              return
            }

            if (selectedPaymentDetails.status === 'reviewed') {
              setOpenSendPaymentConfimationModal(true)
              return
            }

            if (selectedPaymentDetails.status === 'paid' || selectedPaymentDetails.status === 'returned') {
              handleDownloadPaymentVoucher()
              return
            }

            setOpenPaymentDetailsModal(false)
            setSelectedPaymentDetails({})
          }}
          // hideCloseOption={selectedPaymentDetails.status !== 'reviewed'}
          handleCloseOptionAction={() => {
            setErrors({})

            if (selectedPaymentDetails.status === 'reviewed') {
              setWantsToDiscardPayment(true)
              setOpenSendPaymentConfimationModal(true)
              return
            }

            setOpenPaymentDetailsModal(false)
            setSelectedPaymentDetails({})
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setWantsToDiscardPayment(false)
            }

            setOpenPaymentDetailsModal(openState)
            setSelectedPaymentDetails({})
          }}
          closeButtonText={
            selectedPaymentDetails.status === 'reviewed'
              ? t('paymentDetailsModal.discardPayment', { ns: 'document-payments-details-management' })
              : t('paymentDetailsModal.close', { ns: 'document-payments-details-management' })
          }
          hideActionsButtons={
            selectedPaymentDetails.status === 'processing' ||
            (userSession?.role === 'admin-client' && userSession?.belongsTo === config.app.isliveCompanyId && selectedPaymentDetails.status !== 'paid') ||
            (selectedPaymentDetails.status === 'reviewed' && selectedPaymentDetails.documentId !== '') ||
            (selectedPaymentDetails.status === 'reviewed' && userSession && userSession.subRole && userSession.subRole === 'paxum-customer-service-specialist')
          }
          dialogTitle={t('paymentDetailsModal.title', { ns: 'document-payments-details-management' })}
          actionButtonText={
            selectedPaymentDetails.status === 'reviewed'
              ? t('paymentDetailsModal.sendActionButtonText', { ns: 'document-payments-details-management' })
              : selectedPaymentDetails.status === 'paid' || selectedPaymentDetails.status === 'returned'
              ? t('paymentDetailsModal.actionButtonText', { ns: 'document-payments-details-management' })
              : t('paymentDetailsModal.actionButtonText', { ns: 'customer-details-management' })
          }
        >
          <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
        </Dialog>
      )}
      {openPaymentFiltersModal && (
        <Dialog
          dialogIsLoading={loadingCustomersData}
          dialogTitle={t('paymentDetailsModal.title')}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setToDateToSearch(null)
              setFromDateToSearch(null)
              setBeneficiaryToSearch('')
            }
            setOpenPaymentFiltersModal(openState)
          }}
          handleDialogAction={handleFilterCustomerCompanyPayments}
          hideActionsButtons={selectedPaymentDetails.status === 'processing'}
          actionButtonText={t('paymentDetailsModal.actionButtonText', { ns: 'customer-details-management' })}
        >
          <CustomerCompanyPaymentsFilter
            errors={errors}
            setToDateToSearch={setToDateToSearch}
            setFromDateToSearch={setFromDateToSearch}
            customerCompanyPaymentsData={customerCompanyPaymentsData}
            handleModalLoadingState={(loadingStateToSet: boolean) => setLoadingCustomersData(loadingStateToSet)}
          />
        </Dialog>
      )}
      {openSendPaymentConfimationModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle={wantsToDiscardPayment}
          actionButtonSuccessStyle={!wantsToDiscardPayment}
          handleDialogAction={() => {
            if (wantsToDiscardPayment) {
              handleDiscardPayment()
              return
            }

            handleSendPayment()
          }}
          dialogTitle={t('confirmSendPaymentModal.title', { ns: 'payments-to-review-management' })}
          dialogIsLoading={dialogSendPaymentConfimationIsLoading}
          actionButtonText={
            wantsToDiscardPayment ? t('confirmSendPaymentModal.rejectActionButtonText', { ns: 'payments-to-review-management' }) : t('confirmSendPaymentModal.actionButtonText', { ns: 'payments-to-review-management' })
          }
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setWantsToDiscardPayment(false)
            }

            setOpenSendPaymentConfimationModal(openState)
          }}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
      {openPaymentsHistoryModal && (
        <PaymentsHistoryModal
          handleSetModalOpenState={(openState: boolean) => {
            setOpenPaymentsHistoryModal(openState)
          }}
          customerCompanyPaymentsData={customerCompanyPaymentsData}
        />
      )}
    </>
  )
}
